import { useEffect, useMemo, useState } from "react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Copy, Mail, Paperclip, Loader2, PencilLine } from "lucide-react";
import { OutreachLogger } from "@/components/marketing/OutreachLogger";
import { TemplateEditor } from "@/components/marketing/TemplateEditor";
import { fillTemplate as fill } from "@/lib/emailTemplates";
import { downloadPersonalizedAttachment } from "@/lib/attachments";

const copy = async (text, what) => {
  try { await navigator.clipboard.writeText(text); toast.success(`${what} copied`); }
  catch { toast.error("Couldn't copy — select the text and copy it manually"); }
};

export const EmailTemplateDialog = ({ asset, onClose }) => {
  const [me, setMe] = useState(null);
  const [business, setBusiness] = useState("");
  const [contact, setContact] = useState("");
  const [myName, setMyName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attaching, setAttaching] = useState(false);
  const [edited, setEdited] = useState(false);
  const [prospect, setProspect] = useState(null);
  const [bikePath, setBikePath] = useState("");
  const [mine, setMine] = useState(null);
  const [editing, setEditing] = useState(false);
  const channel = asset?.channel || "email";
  const script = channel !== "email";
  const pickProspect = (p) => {
    setProspect(p);
    if (p && !prospect) { setBusiness(p.name || ""); setContact((p.contactPerson || "").split(" ")[0]); if (p.bikePathName) setBikePath(p.bikePathName); }
  };

  useEffect(() => {
    if (!asset) return;
    setBusiness(""); setContact(""); setEdited(false); setMe(null); setMyName(""); setProspect(null); setBikePath(""); setMine(null); setEditing(false);
    Promise.all([api.get("/marketing/personalize/defaults"), api.get("/marketing/templates/mine").catch(() => ({ data: {} }))]).then(([{ data }, { data: t }]) => {
      setMine(t[asset.id] || null);
      setBikePath((cur) => cur || (data.bike_paths || [])[0] || "");
      setMe(data); setMyName((cur) => cur || (data.contact_name && data.contact_name !== data.shop_name ? data.contact_name : ""));
    }).catch(() => setMe({}));
  }, [asset]);
  const vals = useMemo(() => ({ ...(me || {}), my_name: myName.trim(), business: business.trim(), contact: contact.trim(), bike_path: bikePath }), [me, myName, business, contact, bikePath]);
  const tpl = mine || { subject: asset?.email_subject || "", body: asset?.email_body || "" };
  useEffect(() => {
    if (!asset || !me || edited) return;
    setSubject(fill(tpl.subject || "", vals, script)); setBody(fill(tpl.body || "", vals, script));
  }, [asset, me, vals, edited, script, tpl.subject, tpl.body]);

  const attach = async () => {
    setAttaching(true);
    try {
      await downloadPersonalizedAttachment(asset.attach_asset_id, me, myName);
      toast.success("Flyer downloaded — attach it to your email");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Couldn't download the flyer"); }
    finally { setAttaching(false); }
  };
  const mailto = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

  return (
    <Dialog open={!!asset} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="email-template-dialog">
        <DialogHeader>
          <DialogTitle>{asset?.title}</DialogTitle>
          <DialogDescription>{script ? "Filled in with your saved details. Pick the prospect, read it through, then make the call." : "Filled in with your saved flyer details. Add the business, review the wording, then copy it into your email."}{mine ? " Using your edited template." : ""}</DialogDescription>
        </DialogHeader>
        {!me ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div> : (
          editing ? (
            <TemplateEditor asset={asset} script={script} saved={mine} onCancel={() => setEditing(false)} onSaved={(t) => { setMine(t); setEdited(false); setEditing(false); }} />
          ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="template-edit-btn"><PencilLine className="w-4 h-4 mr-1.5" />{mine ? "Edit my template" : "Edit template"}</Button>
            </div>
            <OutreachLogger channel={channel} template={asset?.title} prospect={prospect} onProspect={pickProspect} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="space-y-1.5"><Label>Your name</Label><Input value={myName} onChange={(e) => setMyName(e.target.value)} placeholder="e.g. Jamie Rivera" data-testid="email-my-name-input" /></div>
              <div className="space-y-1.5"><Label>Business name</Label><Input value={business} onChange={(e) => setBusiness(e.target.value)} placeholder="e.g. Harbor Street Café" data-testid="email-business-input" /></div>
              <div className="space-y-1.5"><Label>Contact name (optional)</Label><Input value={contact} onChange={(e) => setContact(e.target.value)} placeholder="e.g. Sam" data-testid="email-contact-input" /></div>
              <div className="space-y-1.5"><Label>Bike path</Label>
                {(me.bike_paths || []).length > 1 ? (
                  <Select value={bikePath} onValueChange={setBikePath}>
                    <SelectTrigger data-testid="email-bike-path-select"><SelectValue placeholder="Choose a bike path" /></SelectTrigger>
                    <SelectContent>{[...new Set([...(me.bike_paths || []), ...(bikePath ? [bikePath] : [])])].map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                  </Select>
                ) : <Input value={bikePath} onChange={(e) => setBikePath(e.target.value)} placeholder="e.g. East Bay Bike Path" data-testid="email-bike-path-input" />}
              </div>
            </div>
            {!script && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>Subject</Label><button type="button" onClick={() => copy(subject, "Subject")} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="email-copy-subject"><Copy className="w-3.5 h-3.5" />Copy</button></div>
              <Input value={subject} onChange={(e) => { setEdited(true); setSubject(e.target.value); }} data-testid="email-subject" />
            </div>
            )}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between"><Label>{script ? "Script" : "Email"}</Label><button type="button" onClick={() => copy(body, script ? "Script" : "Email")} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid="email-copy-body"><Copy className="w-3.5 h-3.5" />Copy</button></div>
              <Textarea rows={16} value={body} onChange={(e) => { setEdited(true); setBody(e.target.value); }} className="font-[inherit] text-sm leading-relaxed" data-testid="email-body" />
              <div className="flex flex-wrap items-center justify-between gap-2">
                {/\[[^\]\n]+\]/.test(body + subject) ? <p className="text-xs text-amber-400" data-testid="email-placeholder-warning">Replace the [bracketed] text before {script ? "calling" : "sending"}.</p> : <span />}
                {edited && <button type="button" onClick={() => setEdited(false)} className="text-xs text-muted-foreground hover:text-primary underline" data-testid="email-reset-template">Reset to template</button>}
              </div>
            </div>
            {asset?.attach_asset_id && (
              <div className="rounded-xl border border-border bg-secondary/40 p-3 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid="email-attachment">
                <div className="text-sm"><b className="flex items-center gap-1.5"><Paperclip className="w-4 h-4 text-primary" />Attach the {asset.attach_title || "flyer"}</b><span className="text-xs text-muted-foreground">Download your personalized copy, then attach it to the email.</span></div>
                <Button variant="outline" onClick={attach} disabled={attaching} data-testid="email-download-attachment">{attaching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Paperclip className="w-4 h-4 mr-2" />}Download flyer</Button>
              </div>
            )}
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              {!script && <Button variant="ghost" asChild><a href={mailto} data-testid="email-open-mail-app"><Mail className="w-4 h-4 mr-2" />Open in email app</a></Button>}
              {prospect?.phone && script && <Button variant="ghost" asChild><a href={`tel:${prospect.phone}`} data-testid="script-call-btn">Call {prospect.phone}</a></Button>}
              <Button onClick={() => copy(script ? body : `Subject: ${subject}\n\n${body}`, script ? "Script" : "Email")} data-testid="email-copy-all"><Copy className="w-4 h-4 mr-2" />{script ? "Copy script" : "Copy subject & email"}</Button>
            </div>
          </div>
          )
        )}
      </DialogContent>
    </Dialog>
  );
};
